/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import Compressor from 'compressorjs'
import { useCallback, useMemo, useState } from 'react'
import DragAndDropArea from '../../../../client/js/components/DragAndDropArea'
import DeleteModal from '../../../../client/js/components/modals/DeleteModal'
import logger from '../../../../client/js/lib/logger'
import {
  deleteEstatePhoto,
  findManyEstatePhotos,
  patchEstatePhoto,
  postEstatePhoto,
  postTempEstatePhoto
} from '../../lib/rest/estatePhotoAPIClient'
import PhotoThumbnail from '../PhotoThumbnail'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getHalEmbedded } from '@jalik/hal'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import { Button, Group, SimpleGrid, Stack } from '@mantine/core'
import { useSettingsContext } from '../SettingsProvider'
import { FormattedMessage, useIntl } from 'react-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from '@fortawesome/free-solid-svg-icons/faStar'
import { notifications } from '@mantine/notifications'

export type EstatePhotosManagerProps = {
  estateId?: number,
  tmpRef: string
}

function EstatePhotosManager (props: EstatePhotosManagerProps) {
  const {
    estateId,
    tmpRef
  } = props

  const intl = useIntl()
  const { settings } = useSettingsContext()

  const photosQuery = useQuery({
    enabled: estateId != null || tmpRef != null,
    queryKey: ['estatePhotos', estateId, tmpRef],
    queryFn: () => findManyEstatePhotos({
      estateId,
      tmpRef,
      limit: 10
    })
  })

  const items = useMemo(() => (
    (photosQuery.data != null ? getHalEmbedded(photosQuery.data)?.photos : null) || []
  ), [photosQuery.data])

  const uploader = useMutation({
    mutationKey: ['uploadEstatePhoto', estateId, tmpRef],
    mutationFn: (file: File) => new Promise((resolve, reject) => {
      if (['image/jpeg', 'image/png'].includes(file.type)) {
        // eslint-disable-next-line no-new
        new Compressor(file, {
          // checkOrientation: true,
          // todo get params from config
          convertSize: 2500,
          maxHeight: 2000,
          maxWidth: 2000,
          retainExif: false,
          error (e) {
            logger.error(`Could not reduce image: ${e.message}`)
            reject(e)
          },
          success (result) {
            if (result instanceof File) {
              resolve(file)
            } else if (result instanceof Blob) {
              resolve(new File([result], result.name, {
                type: result.type,
                lastModified: file.lastModified
              }))
            }
          }
        })
      } else {
        resolve(file)
      }
    })
      .then((optimizedImage: File) => (
        estateId != null
          ? postEstatePhoto(optimizedImage, estateId)
          : postTempEstatePhoto(optimizedImage, tmpRef)
      )),
    onSuccess: () => photosQuery.refetch()
  })

  const highlighter = useMutation({
    mutationKey: ['patchEstatePhoto'],
    mutationFn: (params: Pick<EstatePhoto, 'id' | 'highlighted'>) => patchEstatePhoto(params.id, { highlighted: params.highlighted }),
    onSuccess: () => {
      photosQuery.refetch()
      notifications.hide('highlightedPhoto')
      notifications.show({
        id: 'highlightedPhoto',
        message: intl.formatMessage({
          id: 'highlighted_photo_saved',
          defaultMessage: 'Photo mise en avant enregistrée'
        })
      })
    }
  })
  const { mutate: highlight } = highlighter

  const [itemToDelete, setItemToDelete] = useState<EstatePhoto>(null)

  const deleter = useMutation({
    mutationKey: ['deleteEstatePhoto', itemToDelete],
    mutationFn: () => deleteEstatePhoto(itemToDelete.id),
    onSuccess: () => photosQuery.refetch()
  })

  const handleDelete = useCallback((photo: EstatePhoto) => () => {
    if (!itemToDelete) {
      setItemToDelete(photo)
    } else {
      setItemToDelete(null)
    }
  }, [itemToDelete])

  const handleHiglight = useCallback((photo: EstatePhoto) => () => {
    highlight({ ...photo, highlighted: true })
  }, [highlight])

  const closeDeleteModal = useCallback(() => {
    setItemToDelete(null)
  }, [])

  return (
    <Stack>
      <DragAndDropArea
        accept={settings.PHOTO_FILE_TYPES}
        onUpload={uploader.mutateAsync}
      />

      <LoadingHelper
        query={photosQuery}
        render={() => (
          <SimpleGrid cols={{ base: 2, md: 3, lg: 4, xl: 5 }}>
            {items.map((photo) => (
              <PhotoThumbnail
                key={photo.slug}
                photo={photo}
              >
                <Group gap="xs" justify="center" mt="auto">
                  <Button
                    size="xs"
                    variant="default"
                    onClick={handleHiglight(photo)}
                    title={intl.formatMessage({
                      id: 'highlight_photo',
                      defaultMessage: 'Mettre en avant la photo'
                    })}
                  >
                    <FontAwesomeIcon
                      color={photo.highlighted ? 'orange' : undefined}
                      fixedWidth
                      icon={faStar}
                    />
                  </Button>
                  <Button
                    size="xs"
                    variant="default"
                    onClick={handleDelete(photo)}
                  >
                    <FormattedMessage
                      id="delete"
                      defaultMessage="Supprimer"
                    />
                  </Button>
                </Group>
              </PhotoThumbnail>
            ))}
          </SimpleGrid>
        )}
      />

      {itemToDelete
        ? <DeleteModal
          opened={!!itemToDelete}
          label={itemToDelete?.name}
          onClose={closeDeleteModal}
          onConfirm={deleter.mutateAsync}
          onSuccess={photosQuery.refetch}
        />
        : null}
    </Stack>
  )
}

export default EstatePhotosManager
